import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import {
  LOCALIZATION_DEFAULTS,
  SUPPORTED_LOCALES,
  getLocalizationConfig,
} from '@config/localizationConfig';
import { emitLocalizationWarning } from '@utils/localizationWarnings';

import enUSCommon from './en-US/common.json';
import enUSSpaceCard from './en-US/spaceCard.json';
import enUSRadioCheckbox from './en-US/radioCheckbox.json';
import enUSFeatureCard from './en-US/featureCard.json';
import enUSWelcomeCard from './en-US/welcomeCard.json';
import enUSHomeScreen from './en-US/homeScreen.json';
import enUSDashboard from './en-US/dashboard.json';
import enUSBookingForm from './en-US/bookingForm.json';
import enUSEventDetails from './en-US/eventDetails.json';
import enUSFooter from './en-US/footer.json';
import enUSPlanner from './en-US/planner.json';
import enUSAuth from './en-US/auth.json';
import enUSHeader from './en-US/header.json';
import enUSMetricsReport from './en-US/metricsReport.json';
import enUSReports from './en-US/reports.json';
import frCACommon from './fr-CA/common.json';
import frCASpaceCard from './fr-CA/spaceCard.json';
import frCARadioCheckbox from './fr-CA/radioCheckbox.json';
import frCAFeatureCard from './fr-CA/featureCard.json';
import frCAWelcomeCard from './fr-CA/welcomeCard.json';
import frCAHomeScreen from './fr-CA/homeScreen.json';
import frCADashboard from './fr-CA/dashboard.json';
import frCABookingForm from './fr-CA/bookingForm.json';
import frCAEventDetails from './fr-CA/eventDetails.json';
import frCAFooter from './fr-CA/footer.json';
import frCAPlanner from './fr-CA/planner.json';
import frCAAuth from './fr-CA/auth.json';
import frCAHeader from './fr-CA/header.json';
import frCAMetricsReport from './fr-CA/metricsReport.json';
import frCAReports from './fr-CA/reports.json';

export const supportedLocales = ['en-US', 'fr-CA'] as const;
export type SupportedLocale = (typeof supportedLocales)[number];
export const defaultLocale: SupportedLocale = 'en-US';

const resources = {
  'en-US': {
    common: enUSCommon,
    spaceCard: enUSSpaceCard,
    radioCheckbox: enUSRadioCheckbox,
    featureCard: enUSFeatureCard,
    welcomeCard: enUSWelcomeCard,
    homeScreen: enUSHomeScreen,
    dashboard: enUSDashboard,
    bookingForm: enUSBookingForm,
    eventDetails: enUSEventDetails,
    footer: enUSFooter,
    planner: enUSPlanner,
    auth: enUSAuth,
    header: enUSHeader,
    metricsReport: enUSMetricsReport,
    reports: enUSReports,
  },
  'fr-CA': {
    common: frCACommon,
    spaceCard: frCASpaceCard,
    radioCheckbox: frCARadioCheckbox,
    featureCard: frCAFeatureCard,
    welcomeCard: frCAWelcomeCard,
    homeScreen: frCAHomeScreen,
    dashboard: frCADashboard,
    bookingForm: frCABookingForm,
    eventDetails: frCAEventDetails,
    footer: frCAFooter,
    planner: frCAPlanner,
    auth: frCAAuth,
    header: frCAHeader,
    metricsReport: frCAMetricsReport,
    reports: frCAReports,
  },
};

// ---------------------------------------------------------------------------
// Custom detector — see specs/105-ui-localization-config/contracts/language-override-detector.contract.md
// Activates only when LocalizationConfig.languageOverrideEnabled === true; when
// OFF the lookup returns undefined and the existing detector chain (localStorage
// → navigator) runs exactly as before (SC-003).
// ---------------------------------------------------------------------------
const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: 'localizationConfigOverride',
  lookup: () => {
    const cfg = getLocalizationConfig();
    if (!cfg.languageOverrideEnabled) {
      return undefined;
    }
    if (!(SUPPORTED_LOCALES as readonly string[]).includes(cfg.language)) {
      emitLocalizationWarning({
        code: 'LOCALIZATION_UNSUPPORTED_LANGUAGE',
        configured: cfg.language,
        fallback: LOCALIZATION_DEFAULTS.language,
      });
      return LOCALIZATION_DEFAULTS.language;
    }
    return cfg.language;
  },
  cacheUserLanguage: () => {
    // Intentionally a no-op — the override is config-driven, not user-driven,
    // so it MUST NOT pollute localStorage.
  },
});

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: defaultLocale,
    supportedLngs: supportedLocales,
    // Region-tagged supportedLngs only ('en-US', 'fr-CA'). Without this, i18next
    // also probes the language-only code ('en') and warns "rejecting language
    // code not found in supportedLngs: en". Resolution + fallback still work
    // via fallbackLng.
    load: 'currentOnly',
    defaultNS: 'common',
    ns: [
      'common',
      'spaceCard',
      'radioCheckbox',
      'featureCard',
      'welcomeCard',
      'homeScreen',
      'dashboard',
      'bookingForm',
      'eventDetails',
      'footer',
      'planner',
      'auth',
      'header',
      'metricsReport',
    ],
    detection: {
      order: ['localizationConfigOverride', 'localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'gem-language',
    },
    interpolation: {
      escapeValue: false,
    },
    debug: import.meta.env.VITE_DEBUG_MODE === 'true',
  })

  .catch((error) => {
    console.error('i18n initialization failed:', error);
  });

export async function changeLanguage(locale: SupportedLocale): Promise<void> {
  const cfg = getLocalizationConfig();
  if (cfg.languageOverrideEnabled) {
    emitLocalizationWarning({
      code: 'LOCALIZATION_OVERRIDE_BLOCKED_LANGUAGE_CHANGE',
      attempted: locale,
      active: cfg.language,
    });
    return;
  }
  await i18n.changeLanguage(locale);
}

export function getCurrentLanguage(): SupportedLocale {
  return supportedLocales.includes(i18n.language as SupportedLocale)
    ? (i18n.language as SupportedLocale)
    : defaultLocale;
}

export default i18n;
